'use client';

import { useEffect, useState } from 'react';
import { useTranslation } from 'next-i18next';
import {
  VideoCameraIcon,
  PhoneIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/solid';
import { Button } from '@components/common';

interface InterviewTypeSelectorProps {
  onInterviewTypeSelected: (type: 'video' | 'audio' | 'text') => void;
}

/**
 * InterviewTypeSelector
 *
 * Displays the interview type selection screen allowing users to choose between
 * video, audio, or text interviews.
 *
 * Features:
 * - Shows congratulations message
 * - Displays job card with title and company
 * - Three interview type buttons (Video, Audio, Text)
 * - Audio interview only shows if phone number is configured
 */
export const InterviewTypeSelector = (props: InterviewTypeSelectorProps) => {
  const { onInterviewTypeSelected } = props;
  const { t } = useTranslation('common');
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          onInterviewTypeSelected('video');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [onInterviewTypeSelected]);

  const handleInterviewTypeClick = (type: 'video' | 'audio' | 'text') => {
    onInterviewTypeSelected(type);
  };

  return (
    <div className="flex flex-col items-center p-6 space-y-6">
      {/* Congratulations header */}
      <div className="text-center">
        <div className="mb-4 text-6xl">🎉</div>
        <h2 className="mb-2 text-2xl font-bold text-ink">
          {t('ai.interview.apply.congrats')}
        </h2>
        <p className="text-lg text-ink-medium">
          {t('ai.interview.options.label')}
        </p>
      </div>

      {/* Interview type buttons */}
      <div className="w-full max-w-md space-y-3">
        {/* Video Interview Button */}
        <Button
          onClick={() => handleInterviewTypeClick('video')}
          styling="solid"
          color="secondary"
          rounding="full"
          className="flex items-center justify-center w-full gap-3 py-3"
        >
          <VideoCameraIcon className="size-5" />
          <span className="font-medium">{t('ai.video.interview')}</span>
        </Button>

        {/* Audio Interview Button */}
        <Button
          onClick={() => handleInterviewTypeClick('audio')}
          styling="solid"
          color="primary"
          rounding="full"
          className="flex items-center justify-center w-full gap-3 py-3"
        >
          <PhoneIcon className="size-5" />
          <span className="font-medium">{t('ai.audio.interview')}</span>
        </Button>

        {/* Text Interview Button */}
        <Button
          onClick={() => handleInterviewTypeClick('text')}
          styling="solid"
          color="ink"
          rounding="full"
          className="flex items-center justify-center w-full gap-3 py-3"
        >
          <PencilSquareIcon className="size-5" />
          <span className="font-medium">{t('ai.text.interview')}</span>
        </Button>
      </div>

      {/* Info message */}
      <p className="text-sm text-center text-ink-medium">
        {t('ai.interview.selection.redirect', { sec: countdown })}
      </p>
    </div>
  );
};

